// A newsletter composed in jflo and pasted into Shopify Messaging as one "Code your own" email
// (docs/newsletter-composer-design.md). Product details are live fields by default: the email's Liquid
// reads them from all_products at send time. An override is a value typed for one product in one
// newsletter; it replaces the live field and is kept, marked, until it's reset.

/** A picture on media.jflo.app (Q5), or a Shopify CDN picture. */
export interface NewsletterImage {
  url: string;
  alt: string;
  width?: number;
  height?: number;
  /** For a picture framed in jflo (a saved copy): the original and how it was framed, so it can be reframed. */
  source?: NewsletterImageSource;
}

/** A framed picture's original and its framing. The email uses the saved copy, never the original. */
export interface NewsletterImageSource {
  /** The original: on media.jflo.app or Shopify's CDN. */
  url: string;
  /** The Social picks picture it was chosen from. */
  socialImageId?: string;
  /** Social picks' shot type, for the default framing (product shots shown whole). */
  shotType?: string | null;
  /** The part of the original in the frame, as fractions of it; past 0 to 1 where it's padded. */
  crop: { x: number; y: number; width: number; height: number };
  /** The copy's shape, width over height. */
  ratio: number;
  /** The padding's colour. */
  background: string;
}

/** Where a banner, picture or button goes. Product and collection links stay live. */
export type NewsletterLink =
  | { kind: 'product'; handle: string }
  | { kind: 'collection'; handle: string }
  | { kind: 'url'; url: string };

/** The live fields of a product that can be overridden. Each is plain text written into the email. */
export interface ProductOverrides {
  image?: string;
  brand?: string;
  name?: string;
  /** The whole price line, e.g. "From R899". */
  price?: string;
  /** A featured product's text (the live default is the teaser). */
  text?: string;
}

export type ProductField = keyof ProductOverrides;

export interface NewsletterProduct {
  handle: string;
  overrides?: ProductOverrides;
}

/** Per-block style (Q21). Every key left out comes from Newsletter settings; one set is an override. */
export interface BlockStyle {
  background?: string;
  text?: string;
  /** Button and link colour. */
  accent?: string;
  /** Text on a filled button. */
  accentText?: string;
  font?: NewsletterFont;
  /** Space above and below the block, in px. */
  paddingY?: number;
  /** Space either side of the block, in px. */
  paddingX?: number;
  /**
   * Space either side of the pictures, in px (T-423), on featured product, pictures and video blocks,
   * so a picture can run edge to edge while the text below it keeps paddingX. Left out, it's paddingX.
   */
  imagePaddingX?: number;
  /** Content width inside the 600px email, in px. */
  width?: number;
  align?: 'left' | 'center';
  buttonStyle?: 'filled' | 'outline';
  /** Heading size in px. */
  headingSize?: number;
}

export type BlockStyleKey = keyof BlockStyle;

export const NEWSLETTER_FONTS = ['futura', 'helvetica', 'georgia', 'trebuchet', 'verdana', 'courier'] as const;
export type NewsletterFont = (typeof NEWSLETTER_FONTS)[number];

export const FONT_LABELS: Record<NewsletterFont, string> = {
  futura: 'Futura',
  helvetica: 'Helvetica / Arial',
  georgia: 'Georgia',
  trebuchet: 'Trebuchet',
  verdana: 'Verdana',
  courier: 'Courier',
};

export const FONT_STACKS: Record<NewsletterFont, string> = {
  futura: "Futura, 'Trebuchet MS', Arial, sans-serif",
  helvetica: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  georgia: "Georgia, 'Times New Roman', serif",
  trebuchet: "'Trebuchet MS', Tahoma, sans-serif",
  verdana: 'Verdana, Geneva, sans-serif',
  courier: "'Courier New', Courier, monospace",
};

interface BlockBase {
  id: string;
  style?: BlockStyle;
}

/** Full width, any height (Q18). */
export interface HeroBlock extends BlockBase {
  type: 'hero';
  image: NewsletterImage | null;
  link?: NewsletterLink;
}

/** Full width, cropped to a 3:1 strip (Q18). */
export interface BannerBlock extends BlockBase {
  type: 'banner';
  image: NewsletterImage | null;
  link?: NewsletterLink;
}

/** A section (Q14): optional heading, a product grid, optional "Shop all" button. */
export interface SectionBlock extends BlockBase {
  type: 'section';
  heading?: string;
  columns: 2 | 3;
  products: NewsletterProduct[];
  button?: { label: string; link: NewsletterLink };
}

/** A featured product's picture shape (T-423): cropped from the centre. Left out, the picture's own shape. */
export const FEATURED_PICTURE_SHAPES = ['square', 'wide'] as const;
export type FeaturedPictureShape = (typeof FEATURED_PICTURE_SHAPES)[number];
/** Width over height. */
export const FEATURED_PICTURE_RATIOS: Record<FeaturedPictureShape, { width: number; height: number }> = {
  square: { width: 1, height: 1 },
  wide: { width: 3, height: 2 },
};

/** One big product: picture, name, price, text, button (Q10). */
export interface FeaturedBlock extends BlockBase {
  type: 'featured';
  product: NewsletterProduct | null;
  buttonLabel: string;
  pictureShape?: FeaturedPictureShape;
}

/** Pictures 1 or 2 across, each with optional text and a link below (Q18). */
export interface ImagesBlock extends BlockBase {
  type: 'images';
  columns: 1 | 2;
  items: { image: NewsletterImage | null; text?: string; link?: NewsletterLink }[];
}

/** A picture with the play button drawn in, linking to the video (Q11). */
export interface VideoBlock extends BlockBase {
  type: 'video';
  videoUrl: string;
  thumbnail: NewsletterImage | null;
  /** The thumbnail was made by jflo from the video (not uploaded). */
  thumbnailFromVideo: boolean;
  caption?: string;
}

export interface ParagraphBlock extends BlockBase {
  type: 'paragraph';
  /** Simple rich text: p, strong, em, a, br. */
  html: string;
}

export interface HeadingBlock extends BlockBase {
  type: 'heading';
  text: string;
}

export interface ButtonBlock extends BlockBase {
  type: 'button';
  label: string;
  link: NewsletterLink | null;
}

export const DIVIDER_STROKES = ['solid', 'dashed', 'dotted'] as const;
export type DividerStroke = (typeof DIVIDER_STROKES)[number];

/** Limits for a divider's line. */
export const DIVIDER_THICKNESS = { min: 1, max: 20 } as const;
export const DIVIDER_WIDTH_PERCENT = { min: 10, max: 100 } as const;

/** A line across the email (T-391). Its space and background are the block's style, like any block. */
export interface DividerBlock extends BlockBase {
  type: 'divider';
  stroke: DividerStroke;
  /** The line's thickness, px. */
  thickness: number;
  /** How much of the content width the line spans, %. */
  widthPercent: number;
  /** The line's colour; without one, the settings' muted colour. */
  colour?: string;
  align: 'left' | 'center' | 'right';
}

/** Limits for a spacer's height, px. */
export const SPACER_HEIGHT = { min: 4, max: 200 } as const;

/**
 * Empty space of a set height (T-398). Its colour is the block's Background style, like any block; it
 * has no other style (no space round it, no content width).
 */
export interface SpacerBlock extends BlockBase {
  type: 'spacer';
  /** px. */
  height: number;
}

export type NewsletterBlock =
  | HeroBlock
  | BannerBlock
  | SectionBlock
  | FeaturedBlock
  | ImagesBlock
  | VideoBlock
  | ParagraphBlock
  | HeadingBlock
  | ButtonBlock
  | DividerBlock
  | SpacerBlock;

export type NewsletterBlockType = NewsletterBlock['type'];

export const BLOCK_LABELS: Record<NewsletterBlockType, string> = {
  hero: 'Hero banner',
  banner: 'Small banner',
  section: 'Products',
  featured: 'Featured product',
  images: 'Pictures',
  video: 'Video',
  paragraph: 'Paragraph',
  heading: 'Heading',
  button: 'Button',
  divider: 'Divider',
  spacer: 'Spacer',
};

/** Draft → Copied (the code was copied for Shopify) → Sent (matched to a synced Shopify campaign). */
export type NewsletterStatus = 'draft' | 'copied' | 'sent';

/** A Shopify Email campaign as jflo syncs it (marketing_shopify_email_campaign), with its stats. */
export interface NewsletterCampaign {
  /** marketing_shopify_email_campaign id. */
  id: string;
  title: string;
  sentAt: string | null;
  recipients: number | null;
  views: number | null;
  clicks: number | null;
  orders: number | null;
  /** In the shop's currency. */
  revenue: number | null;
}

/** The campaign a newsletter was sent as, and how it was found: by subject (the sync), or picked by hand. */
export interface NewsletterCampaignLink extends NewsletterCampaign {
  matchedBy: 'subject' | 'manual';
}

export interface Newsletter {
  id: string;
  subject: string;
  previewText: string;
  blocks: NewsletterBlock[];
  /** The logo header from Newsletter settings is in the email (T-398); off for an email that starts with its own. */
  showHeader: boolean;
  /** The colour round the email for this newsletter (T-59), a hex; null uses Newsletter settings' colours.page. */
  pageColour: string | null;
  status: NewsletterStatus;
  copiedAt: string | null;
  /** A fingerprint of what was last copied (newsletterContentHash), for "Changed since you copied it". */
  copiedHash: string | null;
  campaign: NewsletterCampaignLink | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Who saved it last (a name), or null. */
  updatedBy: string | null;
}

export interface NewsletterSocialLink {
  kind: 'facebook' | 'instagram' | 'youtube' | 'tiktok';
  url: string;
  /** A small icon picture; without one the footer uses jflo's own grey icon (socialIconUrl). */
  iconUrl?: string;
}

/** A whole hex colour such as "#1a6b4f" (a newsletter's colour round the email, T-59). */
export const isHexColour = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

/** Per-team header, footer and default style (Q9). */
export interface NewsletterSettings {
  /** The store's address, for links: https://www.bravehardy.com. */
  storeUrl: string;
  logo: NewsletterImage | null;
  /** Logo width in the email, px. */
  logoWidth: number;
  brandName: string;
  tagline: string;
  /** Footer address lines. */
  address: string;
  phone: string;
  email: string;
  socials: NewsletterSocialLink[];
  colours: {
    /** Round the email. */
    page: string;
    /** Behind the content. */
    background: string;
    text: string;
    muted: string;
    accent: string;
    accentText: string;
    sale: string;
  };
  font: NewsletterFont;
  headingFont: NewsletterFont;
  buttonStyle: 'filled' | 'outline';
  /** Button corner radius, px. */
  buttonRadius: number;
  align: 'left' | 'center';
}
